// The enum comes from the proto package directly, not via connect-client: this
// module is bundled into a Node test, and connect-client reads import.meta.env at
// module scope. The type import is erased and costs nothing.
import { FieldComponent } from '@vitesse/proto/vitesse/v1/form_pb.js';
import type { FormFieldSpec } from '../../../core/bridge/connect-client';

/*
 * The pure logic behind <vitesse-add-integration-dialog>'s connection form: which
 * fields are shown, how the one values map is split into plaintext fields and
 * sealed secrets, and what a hidden field's value becomes.
 *
 * Split out of the dialog for the same reason topic-subscription-model.ts is: the
 * SPLIT is a security rule — it decides what goes into the sealed bucket — and a
 * security rule that can only be exercised by driving a browser is a security rule
 * nobody re-checks. bench/integration-form.test.ts covers it in CI.
 *
 * The server does not trust any of this. Since Step 1 of the auth plan it
 * reclassifies every value against the catalog on the way in, so a browser that
 * split wrongly would be corrected, not believed. This is the UI declining to send
 * what it knows is wrong — the layered pattern docs/gated-ui.md prescribes.
 */

/** A field is a secret when the SERVER said so. The widget is only the fallback,
 *  for forms (a plugin's) that predate the flag — and it is exactly the fallback
 *  that breaks when a credential arrives under a widget the browser does not know,
 *  since an unknown component renders as plain text. */
export function isSecret(f: FormFieldSpec): boolean {
  if (f.secret !== undefined) return f.secret;
  return f.component === FieldComponent.SECRET || f.component === FieldComponent.PEM;
}

/** A field with no condition is always shown. One with a condition is shown only
 *  while the field it names holds one of the listed values. */
export function isVisible(f: FormFieldSpec, values: Record<string, string>): boolean {
  const c = f.showWhen;
  if (!c || !c.key) return true;
  return c.values.includes(values[c.key] ?? '');
}

export function visibleFields(fields: FormFieldSpec[], values: Record<string, string>): FormFieldSpec[] {
  return fields.filter((f) => isVisible(f, values));
}

/** The value a field starts at. An explicit default wins; otherwise a SELECT
 *  seeds to its FIRST option (a select with options is never validly empty —
 *  e.g. `auth` starts "none", not ""), everything else starts blank. */
export function defaultValue(f: FormFieldSpec): string {
  if (f.default) return f.default;
  if (f.component === FieldComponent.SELECT && f.options.length > 0) return f.options[0];
  return '';
}

export function seedValues(fields: FormFieldSpec[]): Record<string, string> {
  return Object.fromEntries(fields.map((f) => [f.key, defaultValue(f)]));
}

export interface FieldGroup {
  group: string;
  fields: FormFieldSpec[];
}

/** Fields in catalog order, bucketed by their `group` in first-seen order, so a
 *  section never jumps under the reader when a mode change shows or hides fields
 *  inside it. A field with no group lands in "". */
export function groupFields(fields: FormFieldSpec[]): FieldGroup[] {
  const out: FieldGroup[] = [];
  for (const f of fields) {
    const g = f.group ?? '';
    let bucket = out.find((b) => b.group === g);
    if (!bucket) {
      bucket = { group: g, fields: [] };
      out.push(bucket);
    }
    bucket.fields.push(f);
  }
  return out;
}

export interface ApplyValues {
  fields: Record<string, string>;
  secrets: Record<string, string>;
}

/**
 * Split the form's values into what the apply sends. Three rules, in order of how
 * much they matter:
 *
 *  1. A HIDDEN field's value is DROPPED — a plain field is left out, a secret is
 *     sent as "" (the service reads an explicit "" as "clear this"). Otherwise
 *     switching from bearer to basic still ships the bearer token, stored and
 *     sealed and invisible, for a mode that will never read it.
 *  2. A VISIBLE secret left blank is OMITTED, never sent as "": on an edit the
 *     service keeps the stored value for an omitted secret, which is what "leave
 *     blank to keep the current password" relies on.
 *  3. Which bucket a value goes in is the server's `secret` flag, not the widget.
 */
export function splitForApply(fields: FormFieldSpec[], values: Record<string, string>): ApplyValues {
  const out: ApplyValues = { fields: {}, secrets: {} };
  for (const f of fields) {
    const v = values[f.key] ?? '';
    const secret = isSecret(f);
    if (!isVisible(f, values)) {
      if (secret) out.secrets[f.key] = '';
      continue;
    }
    if (secret) {
      if (v !== '') out.secrets[f.key] = v;
    } else {
      out.fields[f.key] = v;
    }
  }
  return out;
}

/**
 * The fields of the stored integration that an edit in the dialog changes
 * (`update_mask`). The service keeps the rest as stored: the Forwards, the
 * hand-added operations, the description and the enabled state. It reconnects
 * with them, and each Forward continues from its place. A full apply would
 * replace the stored integration with what the dialog shows, and drop them.
 */
export const EDIT_MASK = ['display_name', 'fields', 'secrets'] as const;

export interface ApplyRequest extends ApplyValues {
  integrationId: string;
  type: string;
  displayName: string;
  validateOnly: boolean;
  updateMask?: string[];
}

/**
 * The apply that the dialog sends. An add is a full apply. An edit sends only
 * the fields the dialog shows, under EDIT_MASK. A test of an edit uses the same
 * mask, so the service tests the merged integration, its Forwards included.
 */
export function applyRequest(
  integrationId: string,
  editing: boolean,
  type: string,
  displayName: string,
  form: FormFieldSpec[],
  values: Record<string, string>,
  validateOnly: boolean,
): ApplyRequest {
  const req: ApplyRequest = { integrationId, type, displayName, validateOnly, ...splitForApply(form, values) };
  if (editing) req.updateMask = [...EDIT_MASK];
  return req;
}
